import {
  chmodSync,
  copyFileSync,
  existsSync,
  lstatSync,
  mkdirSync,
  readFileSync,
  readdirSync,
  rmSync,
  statSync,
  symlinkSync,
  writeFileSync,
} from "node:fs";
import { dirname, join } from "node:path";
import type { ExecDeps } from "./exec.js";
import type { BootLogger } from "./types.js";

/**
 * Ports entrypoint.sh:120-143 (`reclaim_home_ownership`), 346-379
 * (`seed_claude_json`), and 381-487 (mount wiring / pod-local ~/.claude.json /
 * credential symlink / opencode links / no-mount fallback).
 *
 * `.claude/rules/claude-session-persistence.md` invariant carried verbatim:
 * ~/.claude.json is pod-local and NEVER symlinked into the mount — the CLI
 * rewrites it wholesale at startup/during runs, and a burst of concurrent pods
 * for the same user+project racing one shared file produces last-writer-wins
 * lost updates (root cause of a live TUI startup-dialog park and a poisoned
 * customApiKeyResponses write bricking every future pod). ~/.claude itself IS
 * symlinked into the mount when present — it holds the shared, append-mostly
 * transcript/plan/memory state --resume depends on.
 */

// ── seed_claude_json ────────────────────────────────────────────────────────

export const SEED_GATES: Record<string, unknown> = {
  hasCompletedOnboarding: true,
  bypassPermissionsModeAccepted: true,
  projects: {
    "/workspaces/repo": { hasTrustDialogAccepted: true },
  },
};

// Fable is entitlement-gated in the CLI's /model picker via
// additionalModelOptionsCache, normally populated by a bootstrap fetch the
// pods' synthesized credentials don't satisfy — without this row the picker
// never offers Fable in a pod. The literal value is entitlement-load-bearing;
// it mirrors FABLE_MODEL ("claude-fable-5") in packages/shared/src/constants/
// models.ts plus the CLI's "[1m]" 1M-context variant suffix.
export const SEED_FABLE_OPTION: {
  value: "claude-fable-5[1m]";
  label: "Fable";
  description: string;
} = {
  value: "claude-fable-5[1m]",
  label: "Fable",
  description: "Fable 5 - most capable for your hardest and longest-running tasks",
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deepMerge(
  target: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...target };
  for (const [k, v] of Object.entries(patch)) {
    const cur = out[k];
    out[k] = isPlainObject(cur) && isPlainObject(v) ? deepMerge(cur, v) : v;
  }
  return out;
}

function hasFableEntry(cache: unknown[]): boolean {
  return cache.some((entry) => {
    if (!isPlainObject(entry)) return false;
    const value = entry.value;
    return typeof value === "string" && value.toLowerCase().includes("fable");
  });
}

/**
 * Pre-answer every interactive first-run gate the CLI shows on a fresh (or
 * partially initialized) config — the onboarding wizard, the
 * bypass-permissions warning, and the folder-trust dialog for the repo
 * workspace — plus append the Fable model-picker entitlement row. Merge via
 * deep-merge, never clobber: CLI-owned keys (cache keys, firstStartTime,
 * other projects' trust entries) survive. A missing or corrupt file becomes a
 * fresh seed (Claude Code treats a 0-byte/unparseable file as fatal, so
 * replacing is a repair, not data loss). Theme only seeds fresh configs — a
 * user-picked theme is never overridden. Never throws: seeding must not kill
 * boot under a single bad stat/write.
 */
export function seedClaudeJson(targetPath: string): void {
  try {
    let config: Record<string, unknown> | null = null;
    try {
      const parsed: unknown = JSON.parse(readFileSync(targetPath, "utf8"));
      if (isPlainObject(parsed)) config = parsed;
    } catch {
      // Missing or corrupt → fresh seed (repair), not a throw.
    }
    let merged = config ? deepMerge(config, SEED_GATES) : { ...SEED_GATES, theme: "dark" };
    const cache = Array.isArray(merged.additionalModelOptionsCache)
      ? merged.additionalModelOptionsCache
      : [];
    if (!hasFableEntry(cache)) {
      merged = { ...merged, additionalModelOptionsCache: [...cache, SEED_FABLE_OPTION] };
    }
    writeFileSync(targetPath, JSON.stringify(merged));
  } catch {
    // Seeding must never kill boot.
  }
}

// ── wire_user_home ──────────────────────────────────────────────────────────

export interface WireUserHomeOptions {
  /** "/home/conveyor" in prod, a tmpdir in tests. */
  home: string;
  /** "/mnt/conveyor-users" — the GCS FUSE CSI mount point. */
  userHomeMount: string;
  userId?: string;
  projectId?: string;
  /** CONVEYOR_SHARED_DIR — always set in split pods. */
  sharedDir?: string;
  /** Pre-created agent log-redirect dir; overridable for tests. */
  scratchDir?: string;
  log: BootLogger;
}

function isDir(path: string): boolean {
  try {
    return statSync(path).isDirectory();
  } catch {
    return false;
  }
}

function isRegularFile(path: string): boolean {
  try {
    return lstatSync(path).isFile();
  } catch {
    return false;
  }
}

function safely(log: BootLogger, label: string, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    log.warn(`[boot] WARN ${label} failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/** `ln -sfn target link` semantics: replace whatever is at `link`, creating
 *  its parent dir first. */
function relink(target: string, link: string): void {
  mkdirSync(dirname(link), { recursive: true });
  rmSync(link, { recursive: true, force: true });
  symlinkSync(target, link);
}

interface CredentialWireOptions {
  home: string;
  root: string;
  sharedDir?: string;
  log: BootLogger;
}

/**
 * Pod-local ~/.claude/.credentials.json: concurrent pods under different keys
 * clobbering one shared file cross-wired usage attribution between accounts
 * (seen live). The shared dir keeps a SYMLINK to an absolute pod-local path,
 * which resolves per-pod; racing pods all write the identical symlink value,
 * so the symlink itself cannot lose data. A pre-existing REGULAR (non-symlink)
 * shared file is legacy — carry its contents into the pod-local target first
 * so auth survives the cutover.
 */
function wireCredentials(opts: CredentialWireOptions): void {
  const { home, root, sharedDir, log } = opts;
  const shared = join(root, ".claude", ".credentials.json");
  const podLocal = sharedDir
    ? join(sharedDir, "claude-credentials.pod.json")
    : join(home, ".claude-credentials.pod.json");
  safely(log, "carry legacy shared credentials", () => {
    if (isRegularFile(shared)) copyFileSync(shared, podLocal);
  });
  safely(log, "repoint credentials symlink", () => relink(podLocal, shared));
  safely(log, "chmod pod-local credentials", () => chmodSync(podLocal, 0o600));
}

/**
 * The agent's memory dir, project-scoped and shared across users.
 *
 * Memory used to be keyed (userId, projectId). The 2026-07-27 fleet audit found
 * 6 such silos holding 67 memories with ZERO topics in common: the same pod-env
 * lessons (chromium `executablePath`, review-guide anchors, which gates OOM)
 * were discovered the hard way and written down independently in each silo,
 * because a memory written by one user's pod is invisible to the next. Project
 * scope is the right boundary — memories are project-technical facts, it
 * matches the ACL boundary, and it mirrors the `users/_shared/graphify`
 * convention already living on this mount.
 *
 * One-file-per-memory keeps concurrent pods collision-free; migration never
 * overwrites an existing shared file, so a stale per-user copy cannot clobber
 * what another pod already published.
 */
const AGENT_MEMORY_PROJECT_DIR = "-workspaces-repo";

function migrateMemories(from: string, to: string, log: BootLogger): void {
  let entries: string[];
  try {
    entries = readdirSync(from);
  } catch {
    return;
  }
  for (const entry of entries) {
    const target = join(to, entry);
    if (existsSync(target)) continue;
    safely(log, `migrate memory ${entry}`, () => copyFileSync(join(from, entry), target));
  }
}

function wireSharedMemory(
  root: string,
  userHomeMount: string,
  projectId: string,
  log: BootLogger,
): void {
  const shared = join(userHomeMount, "users", "_shared", "memory", projectId);
  const link = join(root, ".claude", "projects", AGENT_MEMORY_PROJECT_DIR, "memory");
  safely(log, "mkdir shared memory dir", () => mkdirSync(shared, { recursive: true }));
  // Only migrate a REAL directory — on the second boot `link` is already the
  // symlink we created, and copying it onto itself would be a no-op at best.
  safely(log, "migrate per-user memories", () => {
    if (existsSync(link) && !lstatSync(link).isSymbolicLink()) migrateMemories(link, shared, log);
  });
  safely(log, "wire shared memory symlink", () => relink(shared, link));
}

interface MountedWireOptions {
  home: string;
  userHomeMount: string;
  userId: string;
  projectId: string;
  sharedDir?: string;
  log: BootLogger;
}

function wireMountedUserHome(opts: MountedWireOptions): void {
  const { home, userHomeMount, userId, projectId, sharedDir, log } = opts;
  const root = join(userHomeMount, "users", userId, projectId);
  safely(log, "mkdir user-home root", () => {
    mkdirSync(join(root, ".claude"), { recursive: true });
    mkdirSync(join(root, ".config", "claude"), { recursive: true });
  });
  safely(log, "wire ~/.claude symlink", () => relink(join(root, ".claude"), join(home, ".claude")));
  safely(log, "wire ~/.config/claude symlink", () =>
    relink(join(root, ".config", "claude"), join(home, ".config", "claude")),
  );
  safely(log, "seed pod-local ~/.claude.json", () => {
    rmSync(join(home, ".claude.json"), { recursive: true, force: true });
    seedClaudeJson(join(home, ".claude.json"));
  });
  wireSharedMemory(root, userHomeMount, projectId, log);
  wireCredentials({ home, root, sharedDir, log });
  safely(log, "mkdir opencode root", () => {
    mkdirSync(join(root, ".local", "share", "opencode"), { recursive: true });
    mkdirSync(join(root, ".config", "opencode"), { recursive: true });
  });
  safely(log, "wire opencode data symlink", () =>
    relink(join(root, ".local", "share", "opencode"), join(home, ".local", "share", "opencode")),
  );
  safely(log, "wire opencode config symlink", () =>
    relink(join(root, ".config", "opencode"), join(home, ".config", "opencode")),
  );
}

interface NoMountWireOptions {
  home: string;
  sharedDir?: string;
  log: BootLogger;
}

function wireNoMountUserHome(opts: NoMountWireOptions): void {
  const { home, sharedDir, log } = opts;
  // No persistent home — the CLI reads the pod-local config; seed it there so
  // non-FUSE pods get the same first-run gate suppression as the mounted path.
  safely(log, "seed pod-local ~/.claude.json (no mount)", () =>
    seedClaudeJson(join(home, ".claude.json")),
  );
  if (sharedDir) {
    // Split-mode pods without a persistent user-home: ~/.claude must still
    // cross the container boundary (claude writes transcripts in the
    // workbench; the agent's tailer reads them), so it lives on the shared
    // emptyDir.
    safely(log, "wire ~/.claude to shared emptyDir", () => {
      const target = join(sharedDir, "claude-home", ".claude");
      mkdirSync(target, { recursive: true });
      relink(target, join(home, ".claude"));
    });
  }
}

/**
 * When the pod has the GCS FUSE user-home mount present with a resolvable
 * userId+projectId, symlink ~/.claude, ~/.config/claude, and the opencode
 * dirs into the user's persistent subdir, seed a pod-local ~/.claude.json,
 * and repoint the shared credentials file to a pod-local symlink target.
 * Otherwise fall back to a pod-local-only seed (plus a shared-emptyDir
 * ~/.claude symlink for split pods). Every sub-step degrades independently —
 * one failure never aborts the rest.
 */
export function wireUserHome(opts: WireUserHomeOptions): void {
  const { home, userHomeMount, userId, projectId, sharedDir, log } = opts;
  // Agents habitually redirect gate logs into /tmp/claude-cmd despite docs
  // saying it doesn't exist (55 sessions hit the ENOENT in the 2026-07-27
  // fleet audit) — pre-create it so the redirect just works.
  safely(log, "mkdir agent scratch dir", () =>
    mkdirSync(opts.scratchDir ?? "/tmp/claude-cmd", { recursive: true }),
  );
  if (userId && projectId && isDir(userHomeMount)) {
    wireMountedUserHome({ home, userHomeMount, userId, projectId, sharedDir, log });
  } else {
    wireNoMountUserHome({ home, sharedDir, log });
  }
}

// ── reclaim_home_ownership ──────────────────────────────────────────────────

const RECLAIM_FIND_TIMEOUT_MS = 10_000;

/**
 * Bake steps that run as root while ENV HOME points at the pod home can leave
 * root-owned entries under ~/.config / ~/.local (a uv install receipt once
 * bricked every pod boot fleet-wide). Reclaims them so a baked ownership slip
 * can't EACCES the entrypoint. CONDITIONAL, not a recursive chown: a
 * `find … ! -user conveyor` walk is stat-only when the tree is already clean
 * (the common case), so it triggers no overlayfs copy-up — an unconditional
 * `chown -R` here once cost ~16s of I/O-bound copy-up on every boot even when
 * nothing was root-owned.
 */
export async function reclaimHomeOwnership(
  home: string,
  exec: ExecDeps["exec"],
  log: BootLogger,
): Promise<void> {
  for (const dir of [join(home, ".config"), join(home, ".local")]) {
    if (!isDir(dir)) continue;
    try {
      await exec(
        "sudo",
        [
          "-n",
          "find",
          dir,
          "!",
          "-user",
          "conveyor",
          "-exec",
          "chown",
          "conveyor:conveyor",
          "{}",
          "+",
        ],
        { timeoutMs: RECLAIM_FIND_TIMEOUT_MS },
      );
    } catch (err) {
      log.warn(
        `[boot] WARN reclaim_home_ownership failed for ${dir}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }
}
